import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { 
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { toast } from 'sonner';
import { 
  Send,
  Eye,
  ExternalLink,
  Loader2,
  Mail,
  Clock,
  RefreshCw,
  Users,
  BarChart2,
  CheckCircle2,
  AlertCircle,
  History,
  Ban,
  AlertTriangle,
  MessageCircle,
  Search,
  MapPin,
  Copy,
  Infinity
} from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import { Checkbox } from '../../components/ui/checkbox';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Extract postcode from address for Google search
const extractPostcode = (address) => {
  if (!address) return '';
  const match = address.match(/\b([A-Za-z]{1,2}[0-9][0-9A-Za-z]?\s*[0-9][A-Za-z]{2})\b/i);
  return match ? match[1].toUpperCase() : '';
};

// Build Google search URL for finding business contact/Facebook
const buildGoogleSearchUrl = (businessName, address) => {
  const postcode = extractPostcode(address);
  const searchQuery = postcode 
    ? `${businessName} ${postcode}`
    : businessName;
  return `https://www.google.com/search?q=${encodeURIComponent(searchQuery)}`;
};

// Format address with proper capitalization
const formatAddress = (address) => {
  if (!address) return '';
  return address
    .replace(/\b([A-Za-z]{1,2}[0-9][0-9A-Za-z]?\s*[0-9][A-Za-z]{2})\b/gi, match => match.toUpperCase())
    .split(' ')
    .map(word => word.length <= 4 && word === word.toUpperCase() ? word : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

// Extract area/town from address for FB message
const extractArea = (address) => {
  if (!address) return 'the area';
  // Try to get town/city - usually before postcode
  const parts = address.split(',').map(p => p.trim());
  if (parts.length >= 2) {
    // Get second to last part (usually town), clean up postcode if present
    const townPart = parts[parts.length - 2] || parts[0];
    return townPart.replace(/\b[A-Z]{1,2}[0-9][0-9A-Z]?\s*[0-9][A-Z]{2}\b/gi, '').trim() || 'the area';
  }
  return 'the area';
};

// Generate DM for social media outreach
const generateDM = () => {
  return `Hey! I figured I would drop a message on socials as I know lots of people think emails are spam which is very fair haha! I build websites for independent companies and they're just on basic subscription models to keep the price down - let me know if you'd like a preview link! I can make adjustments too 😊`;
};

const PreviewSent = () => {
  const { token } = useAuth();
  const [websites, setWebsites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [emailHistoryOpen, setEmailHistoryOpen] = useState(null);
  const [emailHistory, setEmailHistory] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchWebsites();
  }, []);

  const fetchWebsites = async () => {
    try {
      const response = await axios.get(`${API}/websites`, { headers });
      // Filter to only show preview_sent status
      const previewSentWebsites = response.data.filter(w => w.status === 'preview_sent');
      setWebsites(previewSentWebsites);
    } catch (error) {
      console.error('Failed to fetch websites:', error);
      toast.error('Failed to load websites');
    } finally {
      setLoading(false);
    }
  };

  const handleResendPreview = async (website) => {
    if (!website.client_email) {
      toast.error('No client email on file');
      return;
    }
    try {
      await axios.post(
        `${API}/websites/send-preview`,
        { website_id: website.id, client_email: website.client_email },
        { headers }
      );
      toast.success('Preview resent successfully!');
      fetchWebsites(); // Refresh to show updated email history
    } catch (error) {
      toast.error('Failed to resend preview');
    }
  };

  const fetchEmailHistory = async (websiteId) => {
    setLoadingHistory(true);
    try {
      const response = await axios.get(`${API}/websites/${websiteId}/email-history`, { headers });
      setEmailHistory(response.data);
      setEmailHistoryOpen(websiteId);
    } catch (error) {
      toast.error('Failed to load email history');
    } finally {
      setLoadingHistory(false);
    }
  };

  const toggleFacebookContacted = async (website) => {
    try {
      const newStatus = !website.facebook_contacted;
      await axios.post(
        `${API}/websites/${website.id}/facebook-contacted?contacted=${newStatus}`,
        {},
        { headers }
      );
      toast.success(newStatus ? 'Marked as contacted on Meta' : 'Meta contact removed');
      fetchWebsites();
    } catch (error) {
      toast.error('Failed to update Meta status');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="preview-sent-page">
      <Card className="bg-card/50 border-white/5">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-purple-500" />
              Preview Sent ({websites.length})
            </CardTitle>
            <CardDescription>
              Websites with previews sent to clients - follow-ups are sent automatically every 7 days
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={async () => {
                try {
                  await axios.post(`${API}/admin/trigger-follow-ups`, {}, { headers });
                  toast.success('Follow-up emails sent!');
                  fetchWebsites();
                } catch (error) {
                  toast.error('Failed to send follow-ups');
                }
              }}
              className="gap-2"
            >
              <Mail className="w-4 h-4" />
              Send Follow-ups
            </Button>
            <Button variant="outline" size="sm" onClick={fetchWebsites}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {websites.length === 0 ? (
            <div className="py-16 text-center">
              <Send className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
              <h3 className="text-lg font-medium mb-2">No previews sent yet</h3>
              <p className="text-muted-foreground max-w-md mx-auto">
                When you send a preview to a client, it will appear here while waiting for their response.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {websites.map((website) => (
                <div
                  key={website.id}
                  className="bg-background/50 border border-white/5 rounded-xl p-5"
                  data-testid={`preview-sent-${website.id}`}
                >
                  <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2 flex-wrap">
                        <h4 className="font-semibold text-lg">{website.business_name}</h4>
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-purple-500/10 text-purple-400">
                          Awaiting Response
                        </span>
                        {/* Email status badges */}
                        {website.email_clicked && (
                          <Badge className="gap-1 bg-green-600">
                            <CheckCircle2 className="w-3 h-3" />
                            Link Clicked
                          </Badge>
                        )}
                        {website.email_opened && !website.email_clicked && (
                          <Badge className="gap-1 bg-yellow-600">
                            <Eye className="w-3 h-3" />
                            Email Opened
                          </Badge>
                        )}
                        {website.sent_via === 'facebook' && (
                          <Badge className="gap-1 bg-blue-500">
                            <Infinity className="w-3 h-3" />
                            Sent via Meta
                          </Badge>
                        )}
                        {website.email_unsubscribed && (
                          <Badge variant="destructive" className="gap-1">
                            <Ban className="w-3 h-3" />
                            Unsubscribed
                          </Badge>
                        )}
                        {website.email_bounced && (
                          <Badge variant="destructive" className="gap-1">
                            <AlertTriangle className="w-3 h-3" />
                            Bounced
                          </Badge>
                        )}
                        {website.follow_up_count > 0 && !website.email_unsubscribed && !website.email_bounced && (
                          <Badge variant="secondary" className="gap-1">
                            <Mail className="w-3 h-3" />
                            Follow-up {website.follow_up_count}/3
                          </Badge>
                        )}
                        {website.facebook_contacted && (
                          <Badge className="gap-1 bg-blue-600">
                            <Infinity className="w-3 h-3" />
                            Meta Contacted
                          </Badge>
                        )}
                      </div>
                      <div className="text-sm text-muted-foreground space-y-1">
                        {/* Address if available */}
                        {website.formatted_address && (
                          <p className="flex items-center gap-2">
                            <MapPin className="w-3 h-3" />
                            {formatAddress(website.formatted_address)}
                          </p>
                        )}
                        {/* Google Search link for finding Facebook/contact */}
                        <a 
                          href={buildGoogleSearchUrl(website.business_name, website.formatted_address)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 text-blue-400 hover:underline"
                        >
                          <Search className="w-3 h-3" />
                          Search on Google
                        </a>
                        <button
                          onClick={() => {
                            const message = generateDM();
                            navigator.clipboard.writeText(message).then(() => {
                              toast.success('DM copied to clipboard!');
                            }).catch(() => {
                              toast.error('Failed to copy message');
                            });
                          }}
                          className="flex items-center gap-2 text-blue-400 hover:underline text-left"
                        >
                          <Copy className="w-3 h-3" />
                          Copy DM
                        </button>
                        <a 
                          href={`https://tryweb.co.uk/preview/${website.subdomain}?_admin=1`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 text-primary hover:underline"
                        >
                          <ExternalLink className="w-3 h-3" />
                          tryweb.co.uk/preview/{website.subdomain}
                        </a>
                        <p className="flex items-center gap-2">
                          <Mail className="w-3 h-3" />
                          Sent to: <span className="text-purple-400">{website.client_email || 'No email'}</span>
                        </p>
                        <p className="flex items-center gap-2">
                          <Clock className="w-3 h-3" />
                          Sent: {formatDate(website.updated_at || website.created_at)}
                        </p>
                        {/* Meta contact checkbox */}
                        <div className="flex items-center gap-2 pt-2">
                          <Checkbox
                            id={`meta-${website.id}`}
                            checked={website.facebook_contacted || false}
                            onCheckedChange={() => toggleFacebookContacted(website)}
                          />
                          <label htmlFor={`meta-${website.id}`} className="text-sm cursor-pointer flex items-center gap-1">
                            <Infinity className="w-3 h-3 text-blue-500" />
                            Contacted on Meta
                          </label>
                        </div>
                        {/* Analytics - Key for tracking if client viewed the preview */}
                        {/* Only show "Not viewed yet" if NO engagement has occurred (no clicks, no opens, no page views) */}
                        {website.analytics && (
                          <div className="flex items-center gap-3 pt-2 mt-2 border-t border-white/5">
                            {website.analytics.total_views > 0 ? (
                              <>
                                <Badge variant="outline" className="border-green-500 text-green-400 gap-1.5">
                                  <CheckCircle2 className="w-3 h-3" />
                                  Viewed
                                </Badge>
                                <span className="flex items-center gap-1.5 text-xs">
                                  <Eye className="w-3.5 h-3.5 text-blue-400" />
                                  <span className="font-medium text-foreground">{website.analytics.total_views}</span> views
                                </span>
                                <span className="flex items-center gap-1.5 text-xs">
                                  <Users className="w-3.5 h-3.5 text-green-400" />
                                  <span className="font-medium text-foreground">{website.analytics.unique_visitors}</span> unique
                                </span>
                                {website.analytics.last_viewed && (
                                  <span className="text-xs">
                                    Last: {new Date(website.analytics.last_viewed).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                                  </span>
                                )}
                              </>
                            ) : !website.email_clicked && !website.email_opened ? (
                              <Badge variant="outline" className="border-yellow-500 text-yellow-400 gap-1.5">
                                <AlertCircle className="w-3 h-3" />
                                Not viewed yet
                              </Badge>
                            ) : null}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Dialog open={emailHistoryOpen === website.id} onOpenChange={(open) => !open && setEmailHistoryOpen(null)}>
                        <DialogTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => fetchEmailHistory(website.id)}
                            className="gap-2"
                          >
                            <History className="w-4 h-4" />
                            Email Log
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-md">
                          <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                              <History className="w-5 h-5" />
                              Email History
                            </DialogTitle>
                            <DialogDescription>
                              {website.business_name}
                            </DialogDescription>
                          </DialogHeader>
                          {loadingHistory ? (
                            <div className="flex justify-center py-8">
                              <Loader2 className="w-6 h-6 animate-spin" />
                            </div>
                          ) : emailHistory && (
                            <div className="space-y-4">
                              {/* Status badges */}
                              <div className="flex flex-wrap gap-2">
                                {emailHistory.email_unsubscribed && (
                                  <Badge variant="destructive">Unsubscribed</Badge>
                                )}
                                {emailHistory.email_bounced && (
                                  <Badge variant="destructive">Email Bounced</Badge>
                                )}
                                {emailHistory.facebook_contacted && (
                                  <Badge className="bg-blue-600">Meta Contacted</Badge>
                                )}
                                <Badge variant="secondary">
                                  Follow-ups: {emailHistory.follow_up_count}/3
                                </Badge>
                              </div>
                              
                              {/* Email timeline */}
                              <div className="space-y-3 max-h-64 overflow-y-auto">
                                {emailHistory.email_history?.length > 0 ? (
                                  emailHistory.email_history.map((entry, idx) => (
                                    <div key={idx} className="border-l-2 border-primary/30 pl-3 py-1">
                                      <p className="font-medium text-sm capitalize">
                                        {entry.type?.replace(/_/g, ' ')}
                                      </p>
                                      {entry.subject && (
                                        <p className="text-xs text-muted-foreground">"{entry.subject}"</p>
                                      )}
                                      <p className="text-xs text-muted-foreground">
                                        {entry.sent_at || entry.at ? formatDate(entry.sent_at || entry.at) : 'Unknown date'}
                                      </p>
                                      {entry.status && (
                                        <Badge variant={entry.status === 'sent' ? 'default' : 'destructive'} className="mt-1 text-xs">
                                          {entry.status}
                                        </Badge>
                                      )}
                                    </div>
                                  ))
                                ) : (
                                  <p className="text-sm text-muted-foreground text-center py-4">No email history yet</p>
                                )}
                              </div>
                            </div>
                          )}
                        </DialogContent>
                      </Dialog>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          // Open preview page in new tab on production domain
                          window.open(`https://tryweb.co.uk/preview/${website.subdomain}?_admin=1`, '_blank');
                        }}
                        className="gap-2"
                      >
                        <Eye className="w-4 h-4" />
                        Preview
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleResendPreview(website)}
                        className="gap-2"
                        disabled={website.email_unsubscribed || website.email_bounced}
                        title={website.email_unsubscribed ? 'User unsubscribed' : website.email_bounced ? 'Email bounced' : ''}
                      >
                        <Send className="w-4 h-4" />
                        Resend
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PreviewSent;
